import { useRef } from 'react';
import { useAccountMargin } from '../../../../hooks/useAccountMargin';
import { useMarketLiquidity } from '../../../../hooks/useMarketLiquidityStore';
import { usePositions } from '../../../../hooks/usePositions';
import { useProductLeverage } from '../../../../hooks/useProductLeverage';
import { useProductPricesSub } from '../../../../hooks/useProductPricesSub';
import { ProductType } from '../../../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../../../hooks/useSelectedSubaccount';
import { alignToValidSize } from '../../../../lib/math';
import { calcMaxSize } from '../../../../lib/maxPositionSize';
import { useTradeFormStore } from '../TradeFormState';
import { useCurrentPosition } from './calculationHooks';

type CachedState = {
  maxSize: bigint;
  orderType: string;
  limitPrice: bigint | undefined;
  reduceOnly: boolean;
  side: string;
  amount: bigint | undefined;
};
/**
 * This hook calculates returns the max position size allowed to be open.
 * To avoid the slider jumping around when the input field is filled we cache the maxSize
 * The max size is affected by the
 * - Oracle price (if user have open positions / orders)
 * - Order book updates as it affect the execution price for market orders
 *
 * If we get an update triggered from the user, e.g changing the order type, limit price, reduce only, side or amount, we update to use the new max size.
 *
 */
export const useMaxSize = (product: ProductType) => {
  const { data: marginData } = useAccountMargin();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const position = useCurrentPosition(positions, product.id);
  const { data: prices } = useProductPricesSub();
  const { oraclePrice } = prices?.[product.id] || {};

  const orderType = useTradeFormStore((s) => s.orderType);
  const limitPrice = useTradeFormStore((s) => s.limitPrice);
  const reduceOnly = useTradeFormStore((s) => s.reduceOnly);
  const side = useTradeFormStore((s) => s.side);
  const amount = useTradeFormStore((s) => s.amount);
  const { value: productLeverage } = useProductLeverage(product);
  const { liquidityData } = useMarketLiquidity(product.id);

  // Use refs to store cached values
  const cachedValues = useRef<CachedState>({
    maxSize: 0n,
    // Track these values to know when to update
    orderType: '',
    limitPrice: undefined,
    reduceOnly: false,
    side: '',
    amount: undefined,
  });

  // Calculate current values
  const currentRawMaxSize = calcMaxSize({
    availableMargin: marginData?.totalAvailable,
    feeRate: product.takerFee,
    initialMarginRatio: 1 / productLeverage,
    maxQuantity: product.maxQuantity,
    oraclePrice: oraclePrice,
    limitPrice: orderType === 'LIMIT' ? limitPrice : undefined,
    currentPositionSize: position.size,
    reduceOnly,
    side,
    lotSize: product.lotSize,
    bidsAsks: liquidityData,
  });
  const currentMaxSize = alignToValidSize(currentRawMaxSize || 0n, product.lotSize);

  // Decide if we should update the cache
  const shouldUpdate =
    cachedValues.current.orderType !== orderType || // Order type changed
    cachedValues.current.limitPrice !== limitPrice || // Limit price changed
    cachedValues.current.reduceOnly !== reduceOnly || // Reduce only changed
    cachedValues.current.side !== side || // Side changed
    cachedValues.current.amount !== amount || // Amount changed
    cachedValues.current.maxSize === 0n;

  if (shouldUpdate) {
    cachedValues.current = {
      maxSize: currentMaxSize,
      orderType,
      limitPrice,
      reduceOnly,
      side,
      amount,
    };
  }

  return cachedValues.current.maxSize;
};
